/**
 * Copyright (c) 2010-2021 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */

import {
    HttpBackend,
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpHeaders,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Observable, Observer } from 'rxjs';
import { Injectable } from '@angular/core';
import { FetchProxy } from './fetch.proxy';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class HttpFetchBackend implements HttpBackend {
    constructor(private fetchProxy: FetchProxy) {}

    /**
     * Processes a request and returns a stream of response events.
     * @param req The request object.
     * @returns An observable of the response events.
     */
    handle(req: HttpRequest<any>): Observable<HttpEvent<any>> {
        // Quick check to give a better error message when a user attempts to use
        // HttpClient.jsonp() without installing the HttpClientJsonpModule
        if (req.method === 'JSONP') {
            throw new Error(`Attempted to construct Jsonp request without HttpClientJsonpModule installed.`);
        }

        if (req.reportProgress) {
            throw new Error('Progress reporting is unavailable with this backend.');
        }

        // Everything happens on Observable subscription.
        return new Observable((observer: Observer<HttpEvent<any>>) => {
            // we can't send a FormData object to postMessage, so we have to do some custom serialization
            let reqBody = null;
            if (req.body instanceof FormData) {
                reqBody = { ...Object.fromEntries(req.body), isFormData: true };
            } else {
                // Serialize the request body if one is present. If not, this will be set to null.
                reqBody = req.serializeBody();
            }

            const init = {
                method: req.method,
                body: reqBody,
                headers: {},
            };

            // Add all the requested headers.
            const headerKeys = req.headers.keys();
            headerKeys.forEach((name) => {
                const values = req.headers.getAll(name);
                init.headers[name] = values.join(',');
            });

            // Add an Accept header if one isn't present already.
            if (!req.headers.has('Accept')) {
                init.headers['Accept'] = 'application/json, text/plain, */*';
            }

            // Auto-detect the Content-Type header if one isn't present already.
            if (!req.headers.has('Content-Type')) {
                const detectedType = req.detectContentTypeHeader();
                // Sometimes Content-Type detection fails.
                if (detectedType !== null) {
                    init.headers['Content-Type'] = detectedType;
                }
            }

            // Next, a few closures are defined for the various events which fetch can
            // emit. This allows them to be unregistered as event listeners later.

            const response$ = this.fetchProxy.send(req.urlWithParams, init, req.responseType);

            response$
                .pipe(
                    map((responseObject) => {
                        if (responseObject.ok) {
                            // A successful response is delivered on the event stream.
                            observer.next(
                                new HttpResponse({
                                    body: responseObject.body,
                                    headers: this.buildHttpHeaders(responseObject.headers),
                                    status: responseObject.status,
                                    statusText: responseObject.statusText,
                                    url: responseObject.url,
                                })
                            );
                            // The full body has been received and delivered, no further events
                            // are possible. This request is complete.
                            observer.complete();
                        } else {
                            // An unsuccessful request is delivered on the error channel.
                            observer.error(
                                new HttpErrorResponse({
                                    // The error in this case is the response body (error from the server).
                                    error: responseObject.body,
                                    headers: this.buildHttpHeaders(responseObject.headers),
                                    status: responseObject.status,
                                    statusText: responseObject.statusText,
                                    url: responseObject.url,
                                })
                            );
                        }
                    })
                )
                .subscribe();
            observer.next({ type: HttpEventType.Sent });

            // The onError callback is called when something goes wrong at the network level.
            // Connection timeout, DNS error, offline, etc. These are actual errors, and are
            // transmitted on the error channel.

            // const handleError = (error: ProgressEvent) => {
            //     const { url } = partialFromXhr();
            //     const res = new HttpErrorResponse({
            //         error,
            //         status: xhr.status || 0,
            //         statusText: xhr.statusText || 'Unknown Error',
            //         url: url || undefined,
            //     });
            //     observer.error(res);
            // };

            // Fire the request, and notify the event stream that it was fired.

            // This is the return from the Observable function, which is the
            // request cancellation handler.
            return () => {};
        });
    }

    private buildHttpHeaders(headers: { [name: string]: any }): HttpHeaders {
        const httpHeaders = new HttpHeaders();
        for (const name in headers) {
            if (Object.hasOwnProperty(name)) {
                httpHeaders.append(name, headers[name]);
            }
        }
        return httpHeaders;
    }
}
